import { callApi } from './api';
import MeditationRoom, { RoomPractice } from '@/models/meditation-room';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const roomsApiUrl = import.meta.env.VITE_API_URL_V1 + '/meditation-rooms/admin/rooms';

export const getMeditationRooms = async () => callApi<{ rooms: MeditationRoom[] }>({ url: roomsApiUrl, method: 'GET' });

export const useMeditationRooms = () => useQuery({ queryKey: ['meditation-rooms'], queryFn: getMeditationRooms });

/**
 * Only editable fields go over the wire — the API's `validate()` rejects
 * unmatched body keys, so echoing back `id`, `created_at` or `deleted` would 400.
 *
 * `key` is here for CREATE only. The update schema does not accept it: it is
 * what every sit ever recorded in the room points at.
 */
export interface CreateRoomPayload {
    key: string;
    name: string;
    tagline?: string;
    practice: RoomPractice;
    categoryId?: string;
    artKey: string;
    orderIndex?: number;
    active?: boolean;
}

export type UpdateRoomPayload = Omit<CreateRoomPayload, 'key'>;

export const useSaveMeditationRoom = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }: { id?: string; payload: CreateRoomPayload | UpdateRoomPayload }) =>
            id
                ? callApi<MeditationRoom>({ url: `${roomsApiUrl}/${id}`, method: 'PUT', data: payload })
                : callApi<MeditationRoom>({ url: roomsApiUrl, method: 'POST', data: payload }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['meditation-rooms'] }),
    });
};

export const useDeleteMeditationRoom = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => callApi<void>({ url: `${roomsApiUrl}/${id}`, method: 'DELETE' }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['meditation-rooms'] }),
    });
};
